/**
 * @fileoverview Provide one shared Word-document selection boundary for picker and file-drop workflows. The parent owns validation feedback and upload execution; this field selects one candidate and delegates through the same callback. `documentFilePolicy.js` supplies format semantics and `DocumentTransferManager.js` supplies transfer context. Selecting or dropping a file does not persist it.
 */

import React, { useRef, useState } from 'react';
import AppIcon from '../shared/AppIcon';
import { isExternalFileDrag } from './DocumentTransferManager';
import { WORD_DOCUMENT_ACCEPT } from './documentFilePolicy';
import './WordDocumentUploadField.css';

 
function WordDocumentUploadField({ file, onFileChange, disabled = false }) {
  const [dragActive, setDragActive] = useState(false);
  const [dropError, setDropError] = useState('');
  const dragDepth = useRef(0);

   
  function selectFiles(files) {
    if (disabled || !files.length) return;
    if (files.length > 1) {
      onFileChange(null);
      setDropError('Drop one Word document at a time.');
      return;
    }
    setDropError('');
    onFileChange(files[0]);
  }

   
  function handleDragEnter(event) {
    if (!isExternalFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    dragDepth.current += 1;
    setDragActive(true);
  }

   
  function handleDragOver(event) {
    if (!isExternalFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
  }

   
  function handleDragLeave(event) {
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragActive(false);
  }

   
  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setDragActive(false);
    selectFiles(Array.from(event.dataTransfer?.files || []));
  }

  return (
    <div className="document-upload-field">
      <label
        className={`document-upload-dropzone${dragActive && !disabled ? ' is-active' : ''}`}
        aria-disabled={disabled}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <AppIcon icon={file ? 'pi pi-file' : 'pi pi-upload'} size={26} aria-hidden="true" />
        <strong aria-live="polite">{dragActive && !disabled ? 'Drop Word document here' : file?.name || 'Drag a Word document here'}</strong>
        <span className="document-upload-dropzone__choose">{file ? 'Choose a different file' : 'or choose a file'}</span>
        <input
          className="document-upload-dropzone__input"
          type="file"
          aria-label="Word file"
          accept={WORD_DOCUMENT_ACCEPT}
          disabled={disabled}
          onChange={(event) => {
            selectFiles(Array.from(event.target.files || []));
            event.target.value = '';
          }} />
      </label>
      {dropError && <div className="documents-archive-error" role="alert">{dropError}</div>}
    </div>
  );
}

export default WordDocumentUploadField;
